import {
  has,
  maybeNativeEnum,
  maybeNumber,
  maybeType,
  timestampToDate,
} from "./helpers";
import { PartialZodInput as ZodFormInput } from "../zod-forms";
import {
  Component, createEffect,
  createSignal,
  createUniqueId,
  For,
  Show,
  VoidComponent,
} from "solid-js";
import { z } from "zod";
import {
  countryNames,
  dispatchPurpose,
  dispatchPurposeNames,
  foreignCountryEU,
  foreignCountryNonEU,
  isCountry,
  paymentType,
} from "~/mydata";
import {
  invoiceMeta,
  isInvoiceEU,
  isInvoiceGR,
  isInvoiceNonEU,
  isRetailInvoiceType,
  salesInvoice,
  salesInvoiceEU,
  salesInvoiceNonEU,
  servicesInvoiceEU,
  servicesInvoiceNonEU,
} from "~/onesign/invoice";
import {
  invoiceDispatch,
  InvoiceSchema,
  invoiceSchema,
  retailCustomer,
  SupportedInvoiceType,
} from "~/schemas/invoices";
import { IssuerInfo } from "~/trpc/api/issuers";
import { createLiveQuery, useRPC } from "./RPCProvider";
import FormRow from "./FormRow";
import FormGroup from "./FormGroup";
import FormLabel from "./FormLabel";
import Button from "./Button";
import NumberFormControl from "./NumberFormControl";
import SelectFormControl from "./SelectFormControl";
import StringFormControl from "./StringFormControl";
import DateFormControl from "./DateFormControl";
import { PartialDeep } from "~/partial-deep";
import { recipientEU, recipientGR, recipientNonEU } from "~/schemas/recipient";
import { createModal } from "./modal";
import { invoiceItemForm } from "./InvoiceItemForm";
import InvoiceItemRowForm from "./InvoiceItemRowForm";
import { Dynamic } from "solid-js/web";
import { LineItem } from "~/schemas/invoice-item";
import { createZodForm, useValidationState } from "~/zod-forms";
import { setProperty } from "dot-prop";
import FormControlErrors from "./FormControlErrors";
import { useNavigate } from "@solidjs/router";
import CheckboxFormControl from "./CheckboxFormControl";
import { Modal } from "solid-bootstrap";
import moment from "moment-timezone";
import { PaymentsTable } from "~/components/PaymentsTable";

type InvoiceInput = ZodFormInput<typeof invoiceSchema>;

const intoDispatchPurpose = maybeNativeEnum(dispatchPurpose);
const intoCountry = maybeType(isCountry);
const [retail, setRetail] = createSignal(false);

export const DraftInvoiceForm: Component<{
  issuer: IssuerInfo;
  draftInvoiceId: string;
  invoiceType: SupportedInvoiceType;
  draft: PartialDeep<InvoiceInput>;
}> = (props) => {
  const navigate = useNavigate();
  const rpc = useRPC();
  const invoiceType: SupportedInvoiceType = props.invoiceType ?? salesInvoice;
  const invoiceSpec = invoiceMeta[invoiceType];
  const taxSeries = props.issuer.taxSeries.filter(
    (t) => t.invoiceType === invoiceType
  );
  const isRetail = isRetailInvoiceType(invoiceType);
  setRetail(isRetail);
  const hasDispatch = invoiceType?.startsWith("sales_invoice");
  const [saveError, setSaveError] = createSignal();

  const initialInput = {
    // taxSeriesId: taxSeries[0]?.id,
    aa: 1,
    payments: [
      {
        type: paymentType.Cash,
        amount: "0",
      },
    ],
    recipient: isRetailInvoiceType(invoiceType)
      ? undefined
      : {
          branch: 0,
          country: isInvoiceGR(invoiceType)
            ? "GR"
            : undefined,
          doy: isInvoiceGR(invoiceType)
            ? ""
            : undefined
        },
    ...props.draft,
    invoiceType: invoiceType,
  };

  if (isInvoiceGR(invoiceType)) {
    setProperty(initialInput, "recipient.country", "GR");
  }

  const [Form, form] = createZodForm({
    schema: invoiceSchema,
    initialInput: initialInput as any,
  });
  const [itemModal, ItemModal] = createModal(true);
  const [modalItemIndex, setModalItemIndex] = createSignal<number | undefined>(
    undefined,
    { equals: false }
  );
  const [invoiceItems, setInvoiceItems] = form.bind("items");
  const [taxSeriesId, setTaxSeriesId] = form.bind("taxSeriesId");
  const [aa, setAA] = form.bind("aa");
  const [emailAddress, setEmailAddress] = form.bind("emailAddress");
  const [sendEmail, setSendEmail] = createSignal(false);
  const [error, setError] = createSignal(false);
  const [errorMessage, setErrorMessage] = createSignal("");
  const [payments, setPayments] = createSignal([]);
  const [paidAmt, setPaidAmt] = createSignal("");
  const [toggleFetchPayments, setToggleFetchPayments] = createSignal(false);

  createEffect(() => {
    console.log("--taxSeriesId--", taxSeriesId())
  }, [taxSeriesId()])

  createEffect(() => {
    const getTotalPaidAmount = (payments: any) => {
      return payments.reduce((total, transaction) => {
        return total + Number(transaction.paymentAmt);
      }, 0).toFixed(2);
    };
    setPaidAmt(getTotalPaidAmount(payments()));
  });


  createEffect(() => {
    const getPayments = async (tp: any) => {
      const p = await rpc.query("getPayments", { id: props.draftInvoiceId });
      setPayments(p);
    }
    getPayments(toggleFetchPayments()).then(() => console.log("payment refreshed"))

  });


  const currentItem = () => {
    const index = modalItemIndex();
    const items = invoiceItems() ?? [];
    return index === undefined ? undefined : items[index];
  };

  const issueInvoice = async (invoice: z.output<InvoiceSchema>) => {
    try {
      console.debug("issueInvoice", { invoice });

      const reply = await rpc.mutation("issueInvoice", {
        invoice,
        recipientEmailAddress: emailAddress(),
        draftInvoiceId: props.draftInvoiceId,
      });
      if(reply) {
        if (reply.statusCode === "Success") {
          navigate(`/dashboard/issuerInvoices`);
        } else {
          setErrorMessage("Το παραστατικό δεν είναι έγκυρο.");
          setError(true);
          console.log(reply.errors);
        }

        console.debug("issueInvoice reply", reply);
        console.debug("issueInvoice reply", reply);
      } else {
        // Update the status of not sent invoices
        // status = myData
        setErrorMessage(`Αδυναμία επικοινωνίας με την ΑΑΔΕ.
        Το παραστατικό σας θα σταλεί αυτόματα μόλις αποκατασταθεί η σύνδεση.
        Μπορείτε να το δείτε στην λίστα παραστατικών.`);
        setError(true);

        // status = offline
        // setErrorMessage(`Αδυναμία επικοινωνίας με την Πάροχο.
        // Το παραστατικό σας θα σταλεί αυτόματα μόλις αποκατασταθεί η σύνδεση.
        // Μπορείτε να το δείτε στην λίστα παραστατικών.`);
        // setError(true);
        await rpc.mutation("updateInvoiceStatus", {});
      }
    } catch (error) {
      // This try catch right now is used only for safety
      // The code that will be executed is in lines 168-172
      // Initially an error was thrown when the url was invalid

      // Update the status of not sent invoices
      // status = myData
      // setErrorMessage(`Αδυναμία επικοινωνίας με την ΑΑΔΕ.
      // Το παραστατικό σας θα σταλεί αυτόματα μόλις αποκατασταθεί η σύνδεση.
      // Μπορείτε να το δείτε στην λίστα παραστατικών.`);
      // setError(true);

      // status = offline
      // setErrorMessage(`Αδυναμία επικοινωνίας με την Πάροχο.
      // Το παραστατικό σας θα σταλεί αυτόματα μόλις αποκατασταθεί η σύνδεση.
      // Μπορείτε να το δείτε στην λίστα παραστατικών.`);
      // setError(true);
      await rpc.mutation("updateInvoiceStatus", {});
    }
  };

  const saveDraftInvoice = async () => {
    try {
      return await rpc.mutation("saveDraftInvoice", {
        issuerId: props.issuer.id,
        draftInvoiceId: props.draftInvoiceId,
        draft: {
          invoiceType,
          ...form.input(),
        },
      });
    } catch (error) {
      setSaveError(error);
    }
  }

  const saveDraft = async () => {
    try {
      await saveDraftInvoice()
      navigate(`/dashboard/issuers/${props.issuer.id}/drafts`);
      // TODO: Show a message for successfully saved
    } catch (error) {
      setSaveError(error);
    }
  };

  const [filters, setFilters] = createSignal({
    issuerId: props.issuer.id
  });
  const [results] = createLiveQuery("listInvoiceItems", filters);
  return (
    <>
      <Modal
        show={error()}
        onHide={() => setError(false)}
        onShow={() => setTimeout(() => setError(false), 6000)}
        classList={{
          ["ps-2"]: true,
          ["pt-2"]: true,
        }}
      >
        <Modal.Body class="p-4 bg-danger text-light">
          <p>{errorMessage()}</p>
        </Modal.Body>
      </Modal>
      <Show when={saveError()}>
        <pre>{JSON.stringify(saveError(), null, 2)}</pre>
      </Show>
      <Form handleSubmit={issueInvoice}>
        <FormRow>
          <FormGroup class="col-md-4">
            <SelectFormControl
              name="invoiceType"
              value={invoiceType}
              label="Τύπος παραστατικού"
              classList={{
                ["p-2"]: true
              }}
              disabled
            >
              <option value={invoiceType}>
                {invoiceSpec.code} - {invoiceSpec.name}
              </option>
            </SelectFormControl>
          </FormGroup>
          <FormGroup class="col-md">
            <StringFormControl
              value={aa()}
              label="AA"
              disabled
              classList={{
                ["p-2"]: true
              }}
            ></StringFormControl>
          </FormGroup>
          <FormGroup class="col-md">
            <StringFormControl
              value={moment
                .utc(new Date())
                .tz("Europe/Athens")
                .format("D/M/YYYY")}
              label="Ημερομηνία"
              disabled
              classList={{
                ["p-2"]: true
              }}
            ></StringFormControl>
          </FormGroup>
          <FormGroup class="col-md-2">
            <SelectFormControl
              value={taxSeriesId() ?? undefined}
              onChange={(e) => {
                setTaxSeriesId(e.currentTarget.value ?? undefined);
                const selectedTaxSeries = props.issuer.taxSeries.filter(
                  (t) => t.invoiceType === invoiceType && t.id === taxSeriesId()
                );
                setAA(selectedTaxSeries[0].sequence.toString());
              }}
              label="Σειρά"
              errors={taxSeriesId.dirty && taxSeriesId.issues}
              classList={{
                ["p-2"]: true
              }}
            >
              <option value="">Επιλέξτε...</option>
              <For each={taxSeries}>
                {(s) => <option value={s.id}>{s.taxSeriesName}</option>}
              </For>
            </SelectFormControl>
          </FormGroup>
          <FormGroup class="col">
            <FormLabel for="saveDraft">&nbsp;</FormLabel>
            <Button
              name="saveDraft"
              disabled={
                form.submitting() ||
                form.lastSubmit()?.success === false ||
                !form.valid()
              }
              kind="primary"
              class="form-control"
              onClick={async (e) => {
                e.preventDefault();
                await saveDraft();
                form.submit(() => {
                });
              }}
            >
              Αποθήκευση
            </Button>
          </FormGroup>

          <FormGroup class="col me-4">
            <FormLabel for="issue">&nbsp;</FormLabel>
            <Button
              name="issueInvoice"
              disabled={
                form.submitting() ||
                form.lastSubmit()?.success === false ||
                !form.valid()
              }
              kind="success"
              type="submit"
              class="form-control"
            >
              Έκδοση
            </Button>
          </FormGroup>
        </FormRow>
        <div class="d-flex justify-content-end align-items-center flex-wrap">
          <div class="row me-3 mt-4 align-items-center">
            <FormGroup class="col me-4">
              <CheckboxFormControl
                label="Αποστολή μέσω email"
                checked={sendEmail()}
                onChange={(e) => setSendEmail(e.currentTarget.checked)}
                classList={{
                  ["p-2"]: true
                }}
              ></CheckboxFormControl>
            </FormGroup>
            <FormGroup class="col me-4">
              <StringFormControl
                label="Διεύθυνση Email"
                value={emailAddress()}
                onChange={(evt) => setEmailAddress(evt.currentTarget.value)}
                disabled={!sendEmail()}
                classList={{
                  ["p-2"]: true
                }}
              ></StringFormControl>
            </FormGroup>
          </div>
        </div>
        {isRetail ? (
          <RetailCustomerFieldset />
        ) : (
          <RecipientFieldset invoiceType={invoiceType} />
        )}
        <div class={"col me-4 pt-4 pb-4"}>
          <PaymentsTable
            payments={payments()}
          />
        </div>
        <Show when={hasDispatch}>
          <DispatchFieldset
            initiallyOpen={
              has(props.draft, "dispatch") && props.draft.dispatch !== undefined
            }
          />
        </Show>
        <fieldset class="form-group me-4">
          <legend class="ms-3">Γραμμές παραστατικού</legend>
          <Button
            class="mt-2 mb-4 ms-3"
            kind="primary"
            id="add-item-btn"
            classList={{
              "is-invalid": invoiceItems.issues.length > 0
            }}
            onClick={(e) => {
              e.preventDefault();
              setModalItemIndex(undefined);
              itemModal.show();
            }}
          >
            Προσθήκη είδους
          </Button>
          <FormControlErrors for="add-item-btn" errors={invoiceItems.issues} />
        </fieldset>
        <InvoiceItemRowForm
          items={invoiceItems() ?? []}
          editItem={(index) => {
            setModalItemIndex(index);
            itemModal.show();
          }}
          deleteItem={(index) => {
            setInvoiceItems((items = []) => [
              ...items.slice(0, index),
              ...items.slice(index + 1)
            ]);
          }}
          draftInvoiceId={props.draftInvoiceId}
          invoiceType={props.invoiceType}
          issuerVatNumber={props.issuer.vatNumber}
          taxSeriesId={taxSeriesId()}
          saveDraftInvoice={saveDraftInvoice}
          setToggleFetchPayments={setToggleFetchPayments}
          toggleFetchPayments={toggleFetchPayments()}
          paidAmt={paidAmt}
        ></InvoiceItemRowForm>
      </Form>
      <ItemModal
        centered
        dialogClass="w100per justify-content-center"
        contentClass="w80per mobile-w100per p-3"
        size="sm"
      >
        {/* <ItemModal.Header>Γραμμή παραστατικού</ItemModal.Header> */}
        <ItemModal.Body>
          <Dynamic
            component={invoiceItemForm(
              invoiceType,
              currentItem()?.itemType ?? LineItem
            )}
            index={modalItemIndex()}
            item={currentItem()}
            products={results.latest?.results}
            onSubmit={(item: any, index?: number) => {
              setInvoiceItems((items = []) =>
                index === undefined
                  ? [...items, item]
                  : [...items.slice(0, index), item, ...items.slice(index + 1)]
              );
              setModalItemIndex(undefined);
              itemModal.hide();
            }}
          />
        </ItemModal.Body>
        {/* <ItemModal.Footer>Save / Cancel</ItemModal.Footer> */}
      </ItemModal>
    </>
  );
};

export default DraftInvoiceForm;

const DispatchFieldset: Component<{ initiallyOpen: boolean }> = (props) => {
  const [open, setOpen] = createSignal(props.initiallyOpen);
  // Use local schema
  const dispatchFieldsetSchema = z.object({
    dispatch: invoiceDispatch,
  });
  const form = useValidationState<typeof dispatchFieldsetSchema>();
  const [purpose, setPurpose] = form.bind("dispatch.purpose");
  const [sentAt, setSentAt] = form.bind("dispatch.sentAt");
  const [dispatchTime, setDispatchTime] = form.bind("dispatch.dispatchTime");
  const [vehicleNumber, setVehicleNumber] = form.bind("dispatch.vehicleNumber");
  const [locationAddress, setLocationAddress] = form.bind(
    "dispatch.locationAddress"
  );
  const id = createUniqueId();

  return (
    <fieldset class="form-group mt-3 mb-3 me-3">
      <legend class="form-group-label">
        <label
          for={id}
          class="form-check-label ms-3 me-2"
          classList={{
            "text-muted": !open(),
          }}
        >
          Δελτίο Αποστολής
        </label>
        <input
          id={id}
          type="checkbox"
          class="form-check-input mt-1"
          checked={open()}
          onChange={(e) => setOpen(e.currentTarget.checked)}
        />
      </legend>
      <Show when={open()}>
        <FormRow>
          <FormGroup class="col-sm-3">
            <SelectFormControl
              label="Σκοπός διακίνησης"
              value={purpose()}
              onChange={(e) =>
                setPurpose(intoDispatchPurpose(e.currentTarget.value))
              }
              errors={purpose.dirty && purpose.issues}
              classList={{
                ["p-2"]: true,
              }}
            >
              <option value="">Επιλέξτε...</option>
              <For each={Object.entries(dispatchPurposeNames)}>
                {([id, name]) => <option value={id}>{name}</option>}
              </For>
            </SelectFormControl>
          </FormGroup>
          <FormGroup class="col">
            <DateFormControl
              label="Ημερομηνία Αποστολής"
              value={timestampToDate(sentAt())}
              onChange={(e) =>
                setSentAt(e.currentTarget.valueAsDate ?? undefined)
              }
              errors={sentAt.dirty && sentAt.issues}
              type="date"
              classList={{
                ["p-2"]: true,
              }}
            />
          </FormGroup>
          <FormGroup class="col-sm-2">
            <StringFormControl
              label="Ώρα Αποστολής"
              value={dispatchTime()}
              onChange={(e) => setDispatchTime(e.currentTarget.value)}
              errors={dispatchTime.dirty && dispatchTime.issues}
              classList={{
                ["p-2"]: true,
              }}
            />
          </FormGroup>
          <FormGroup class="col-sm-2">
            <StringFormControl
              label="Αρ. Οχήματος"
              value={vehicleNumber()}
              onChange={(e) => setVehicleNumber(e.currentTarget.value)}
              errors={vehicleNumber.dirty && vehicleNumber.issues}
              classList={{
                ["p-2"]: true,
              }}
            />
          </FormGroup>
          <FormGroup class="col">
            <StringFormControl
              label="Διεύθυνση Παράδοσης"
              value={locationAddress()}
              onInput={(e) => setLocationAddress(e.currentTarget.value)}
              errors={locationAddress.dirty && locationAddress.issues}
              classList={{
                ["p-2"]: true,
              }}
            ></StringFormControl>
          </FormGroup>
        </FormRow>
      </Show>
    </fieldset>
  );
};

const RetailCustomerFieldset: VoidComponent = () => {
  const schema = z.object({
    retailCustomer: retailCustomer,
  });
  const form = useValidationState<typeof schema>();

  const [name, setName] = form.bind("retailCustomer.name");
  const [notes, setNotes] = form.bind("retailCustomer.notes");
  const [vatNumber, setVATNumber] = form.bind("retailCustomer.vatNumber");
  return (
    <fieldset class="form-group me-4">
      <legend class="form-group-label ms-3 mb-3">
        Στοιχεία Πελατη Λιανικής
      </legend>
      <FormGroup class="me-4">
        <StringFormControl
          value={name()}
          onInput={(e) => setName(e.currentTarget.value)}
          type="text"
          label="Ονοματεπώνυμο"
          errors={name.dirty && name.issues}
          classList={{
            ["p-2"]: true,
          }}
        />
      </FormGroup>
      <FormRow>
        <FormGroup class="col-3">
          <StringFormControl
            value={vatNumber()}
            errors={vatNumber.dirty && vatNumber.issues}
            onInput={(e) => setVATNumber(e.currentTarget.value)}
            label="Α.Φ.Μ"
            type="text"
            classList={{
              ["p-2"]: true,
            }}
            required
          />
        </FormGroup>
      </FormRow>
      <FormRow>
        <FormGroup class="col">
          <StringFormControl
            label="Σημειώσεις"
            value={notes()}
            onChange={(e) => setNotes(e.currentTarget.value)}
            errors={notes.dirty && notes.issues}
            classList={{
              ["p-2"]: true,
            }}
          ></StringFormControl>
        </FormGroup>
      </FormRow>
    </fieldset>
  );
};

const RecipientFieldset: Component<{ invoiceType: SupportedInvoiceType }> = ({
  invoiceType,
}) => {
  const recipientSchema = z.object({
    recipient: z.union([recipientGR, recipientEU, recipientNonEU]),
  });
  const form = useValidationState<typeof recipientSchema>();
  const isEU = isInvoiceEU(invoiceType);
  const isNonEU = isInvoiceNonEU(invoiceType);
  const isForeign = isEU || isNonEU;
  const countries = isEU
    ? foreignCountryEU
    : isNonEU
    ? foreignCountryNonEU
    : (["GR"] as const);
  const [name, setName] = form.bind("recipient.name");
  const [country, setCountry] = form.bind("recipient.country");
  const [vatNumber, setVATNumber] = form.bind("recipient.vatNumber");
  const [postalCode, setPostalCode] = form.bind("recipient.address.postalCode");
  const [city, setCity] = form.bind("recipient.address.city");
  const [street, setStreet] = form.bind("recipient.address.street");
  const [streetNumber, setStreetNumber] = form.bind("recipient.address.number");
  const [branch, setBranch] = form.bind("recipient.branch");
  const [notes, setNotes] = form.bind("recipient.notes");
  const [doy, setDoy] = form.bind("recipient.doy");

  const [filters, setFilters] = createSignal({});
  const [results] = createLiveQuery("listClients", filters);
  return (
    <fieldset class="form-group me-4">
      <legend class="form-group-label ms-3 mb-3">
        Στοιχεία Λήπτη Παραστατικού
      </legend>
      <FormGroup class="me-4">
        <SelectFormControl
          label="Πελάτες"
          classList={{
            ["p-2"]: true,
            ["mb-2"]: true,
          }}
          onChange={(e) => {
            if (!e.currentTarget.value) {
              setName("");
              setCountry("GR");
              setVATNumber("");
              setBranch(0);
              setPostalCode("");
              setCity("");
              setStreet("");
              setStreetNumber(undefined);
              setDoy("");
              return;
            }

            const index = Number.parseInt(e.currentTarget.value);
            const client = results.latest?.results[index];

            setName(client?.clientName);
            setCountry(client?.country);
            setVATNumber(client?.vatNumber);
            setBranch(client?.branch);
            setPostalCode(client?.postalCode);
            setCity(client?.city);
            setStreet(client?.addressStreet);
            setStreetNumber(client?.addressNumber);
            setDoy(client?.doy);
          }}
        >
          <option value="">Επιλέξτε...</option>
          <For each={results.latest?.results}>
            {(client, index) => {
              return <option value={index()}>{client.clientName}</option>;
            }}
          </For>
        </SelectFormControl>
      </FormGroup>
      <FormGroup class="me-4">
        <StringFormControl
          value={name()}
          onInput={(e) => setName(e.currentTarget.value)}
          type="text"
          required={isForeign}
          label="Επωνυμία"
          errors={name.dirty && name.issues}
          classList={{
            ["p-2"]: true,
          }}
        />
      </FormGroup>
      <FormRow>
        <FormGroup class="col">
          <SelectFormControl
            value={country()}
            errors={country.dirty && country.issues}
            onChange={(e) => setCountry(intoCountry(e.currentTarget.value))}
            required
            disabled={!isForeign}
            label={isEU ? "Χώρα Ε.Ε." : isNonEU ? "Χώρα εκτός Ε.Ε." : "Χώρα"}
            classList={{
              ["p-2"]: true,
            }}
          >
            <For each={countries}>
              {(c) => (
                <option value={c}>
                  {c} - {countryNames[c]}{" "}
                </option>
              )}
            </For>
          </SelectFormControl>
        </FormGroup>
        <FormGroup class="col-3">
          <StringFormControl
            value={vatNumber()}
            errors={vatNumber.dirty && vatNumber.issues}
            onInput={(e) => setVATNumber(e.currentTarget.value)}
            label="Α.Φ.Μ"
            type="text"
            classList={{
              ["p-2"]: true,
            }}
            required
          />
        </FormGroup>
        <Show
          when={
            !retail() &&
            invoiceType !== salesInvoiceEU &&
            invoiceType !== salesInvoiceNonEU &&
            invoiceType !== servicesInvoiceEU &&
            invoiceType !== servicesInvoiceNonEU
          }
        >
          <FormGroup class="col-3">
            <StringFormControl
              value={doy()}
              errors={doy.dirty && doy.issues}
              onInput={(e) => setDoy(e.currentTarget.value)}
              label="Δ.Ο.Υ."
              type="text"
              classList={{
                ["p-2"]: true,
              }}
              required
            />
          </FormGroup>
        </Show>
        <Show when={!retail()}>
          <FormGroup class="col">
            <SelectFormControl
              label="Υποκατάστημα"
              value={branch()}
              onChange={(e) =>
                setBranch(Number.parseInt(e.currentTarget.value) ?? 0)
              }
              errors={branch.dirty && branch.issues}
              classList={{
                ["p-2"]: true,
              }}
            >
              <option value="0">Όχι</option>
              <option value="1">Ναι</option>
            </SelectFormControl>
          </FormGroup>
        </Show>
      </FormRow>
      <Show when={!retail()}>
        <FormRow>
          <FormGroup class="col">
            <StringFormControl
              value={postalCode()}
              errors={postalCode.dirty && postalCode.issues}
              onChange={(e) => setPostalCode(e.currentTarget.value)}
              type="text"
              required={isForeign}
              label="T.K."
              classList={{
                ["p-2"]: true,
              }}
            />
          </FormGroup>
          <FormGroup class="col">
            <StringFormControl
              value={city()}
              onChange={(e) => setCity(e.currentTarget.value)}
              type="text"
              label="Πόλη"
              classList={{
                ["p-2"]: true,
              }}
            />
          </FormGroup>
          <FormGroup class="col">
            <StringFormControl
              value={street()}
              onChange={(e) => setStreet(e.currentTarget.value)}
              type="text"
              required={isForeign}
              label="Οδός"
              classList={{
                ["p-2"]: true,
              }}
            />
          </FormGroup>
          <FormGroup class="col">
            <NumberFormControl
              value={streetNumber()}
              onChange={(e) =>
                setStreetNumber(maybeNumber(e.currentTarget.value))
              }
              type="number"
              min="1"
              label="Αριθμός"
              classList={{
                ["p-2"]: true,
              }}
            />
          </FormGroup>
        </FormRow>
      </Show>
      <FormRow>
        <FormGroup class="col">
          <StringFormControl
            label="Σημειώσεις"
            value={notes()}
            onChange={(e) => setNotes(e.currentTarget.value)}
            errors={notes.dirty && notes.issues}
            classList={{
              ["p-2"]: true,
            }}
          ></StringFormControl>
        </FormGroup>
      </FormRow>
    </fieldset>
  );
};
